import React, { useEffect, useMemo, useState } from "react";
import { FaMicrochip, FaBuilding, FaCity, FaStore } from "react-icons/fa";
import NumberFlow from "@number-flow/react";
import { PerformanceBadge } from "../ProductConsumentCard";
import MascotLoadingOverlay from "../../shared/MascotLoadingOverlay";
import { AppLink } from "../../shared";
import mascotNoData from "../../../Assets/icons/okkali_mascot_nodata.png";
import coffeeBean from "../../../Assets/icons/coffee_bean.png";
import teaLeaf from "../../../Assets/icons/tea_leaf.png";
import { FIRM_TYPE_STYLES } from "../../../utils/firmTypeStyles";

// NumberFlow prop'ları modül sabiti (#233): satır içi nesneler her render'da
// yeni referans olup biçimlendiriciyi / zamanlamayı yeniden kurduruyordu.
const NUMBER_FORMAT = { useGrouping: true };
const NUMBER_TIMING = { duration: 500, easing: "ease-out" };

// Tüketimler kartındaki ürün sırası ve ikonlarıyla aynı
const PRODUCTS = [
  { name: "Çay", label: "Çay", icon: teaLeaf },
  { name: "Türk Kahvesi", label: "Türk K.", icon: coffeeBean },
  { name: "Filtre Kahve", label: "Filtre", icon: coffeeBean },
  { name: "Espresso Bazlı", label: "Espresso", icon: coffeeBean },
];

// Satırın toplanacağı üst firma (#205): sunucunun grup kökü — zincirin tepesi;
// bayiye bağlı müşteride bayi. Eski payload'larda yalnız mainFirm* vardır.
const groupOf = (row) => {
  if (row.groupFirmId) {
    return {
      id: row.groupFirmId,
      name: row.groupFirmName,
      bayserNo: row.groupFirmBayserNo,
      type: row.groupFirmType || null,
    };
  }
  if (row.mainFirmId) {
    return { id: row.mainFirmId, name: row.mainFirmName, bayserNo: row.mainFirmBayserNo, type: null };
  }
  return null;
};

const PERIOD_LABELS = {
  today: "Bugün",
  yesterday: "Dün",
  thisweek: "Bu Hafta",
  thismonth: "Bu Ay",
  thisyear: "Bu Yıl",
};

// Sabit boyutlu liste — sayfa başına satır sayısı
const PAGE_SIZE = 5;

// dashboardData payload'ındaki kırılım anahtarları "Espresso" kullanır
const SERVER_PRODUCT_KEYS = { "Espresso Bazlı": "Espresso" };

/**
 * Cihaz/Firma bazlı tüketim kırılımı box'ı. Veri, Tüketimler kartıyla AYNI
 * dashboardData payload'ından gelir (deviceBreakdown/firmBreakdown) — tek
 * yükleme, tek loading; ürün/dönem geçişleri tamamen yerel ve anlıktır.
 * Sağ üstte kayan (sliding pill) animasyonlu ürün butonları vardır; toplam
 * tüketim butonların üzerinde yazar (ayrıca tekrarlanmaz). Liste sayfalıdır.
 */
function ConsumptionBreakdownCard({ mode, period, consumption, performance, breakdown, isLoading }) {
  const isDevices = mode === "devices";
  const [activeProduct, setActiveProduct] = useState(PRODUCTS[0].name);
  const [page, setPage] = useState(1);
  // ANA FİRMADA TOPLA (varsayılan AÇIK, mor ikon): şubelerin tüketimi üst
  // firmasının satırında toplanır — satırda ana firmanın adı/bayser no'su
  // yazar ve tıklayınca ana firmaya gidilir. Kapatılınca (mavi ikon) her
  // firma kendi satırında görünür, üst firması rozet olarak yazar.
  const [groupByMainFirm, setGroupByMainFirm] = useState(true);

  // Ürün/dönem değişince ilk sayfaya dön
  useEffect(() => {
    setPage(1);
  }, [activeProduct, period, groupByMainFirm]);

  const isFetching = !!isLoading;
  const serverProductKey = SERVER_PRODUCT_KEYS[activeProduct] || activeProduct;
  const rawRows = useMemo(
    () => breakdown?.[period]?.[serverProductKey] || [],
    [breakdown, period, serverProductKey]
  );

  // Ana firma kırılımı — yalnız firma modunda anlamlı.
  const rows = useMemo(() => {
    if (isDevices || !groupByMainFirm) return rawRows;
    const buckets = new Map();
    rawRows.forEach((row) => {
      // Üst firması olmayan firma kendi başına bir "grup"tur. Bayi de ana
      // firma gibi davranır: bağlı müşterileri onun satırında toplanır (#205).
      const group = groupOf(row);
      const key = group?.id || row.firmId;
      const bucket = buckets.get(key);
      const isChild = !!group;
      if (!bucket) {
        buckets.set(key, {
          firmId: key,
          firmName: isChild ? group.name : row.firmName,
          bayserNo: isChild ? group.bayserNo : row.bayserNo,
          firmType: isChild ? group.type : row.firmType,
          // Toplanmış satırda üst firma rozeti gösterilmez.
          mainFirmName: null,
          mainFirmBayserNo: null,
          deviceCount: row.deviceCount || 0,
          consumption: row.consumption || 0,
          previous: row.previous || 0,
          hasPrevious: !!row.hasPrevious,
          // Kaç firmanın toplandığı — satırda küçük bir bilgi olarak durur.
          firmCount: 1,
          // Satır bir ANA FİRMA mı: altında en az bir şube toplandıysa evet.
          // Sadece firmCount'a bakmak yetmiyordu — ana firmanın kendi tüketimi
          // yoksa bucket'ta tek satır (şube) kalıyor ve ikon mavi görünüyordu.
          hasChildren: isChild,
        });
        return;
      }
      bucket.deviceCount += row.deviceCount || 0;
      bucket.consumption += row.consumption || 0;
      bucket.previous += row.previous || 0;
      bucket.hasPrevious = bucket.hasPrevious || !!row.hasPrevious;
      bucket.firmCount += 1;
      if (isChild) bucket.hasChildren = true;
      // Ana firmanın KENDİ satırı da geldiyse ad/bayser onunla netleşir.
      if (!isChild) {
        bucket.firmName = row.firmName;
        bucket.bayserNo = row.bayserNo;
        bucket.firmType = row.firmType || bucket.firmType;
      }
    });
    return [...buckets.values()]
      .map((b) => ({
        ...b,
        // Yüzde toplanan değerlerden yeniden hesaplanır; sunucudaki satır
        // yüzdesi toplama sonrası geçersizdir.
        changePercent: !b.hasPrevious
          ? undefined
          : b.previous === 0
            ? b.consumption > 0
              ? null
              : 0
            : Math.round(((b.consumption - b.previous) / b.previous) * 100),
      }))
      .sort((a, b) => b.consumption - a.consumption);
  }, [rawRows, isDevices, groupByMainFirm]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedRows = rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Ürün butonlarındaki toplam/ortalama — Tüketimler kartıyla aynı kaynak (socket verisi)
  const consumptionByProduct = useMemo(() => {
    const map = {};
    (consumption || []).forEach((item) => {
      map[item.productName] = item;
    });
    return map;
  }, [consumption]);

  const activeIndex = Math.max(0, PRODUCTS.findIndex((p) => p.name === activeProduct));
  const Icon = isDevices ? FaMicrochip : FaBuilding;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 flex flex-col overflow-hidden">
      {/* Header — solda başlık, sağda kayan animasyonlu ürün filtre butonları */}
      <div className="bg-gray-50 p-3 sm:p-4 border-b border-gray-200 shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-stretch gap-2.5 sm:gap-3">
          <div className="flex items-stretch gap-2 sm:gap-3 min-w-0 shrink-0">
            {/* İkon çipi filtre butonları barıyla aynı yükseklikte (stretch).
                FİRMA modunda aynı zamanda ANAHTAR: mor + çoklu bina ikonu =
                şubeler ana firmada toplanır, mavi + tek bina = her firma
                kendi satırında (üst firması rozette). */}
            {isDevices ? (
              <div className="flex items-center justify-center px-2.5 sm:px-3 bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-100 rounded-lg sm:rounded-xl shadow-sm border">
                <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setGroupByMainFirm((v) => !v)}
                title={
                  groupByMainFirm
                    ? "Şubeler ana firmada, bayi müşterileri bayide toplanıyor — firmaları tek tek görmek için tıkla"
                    : "Firmalar tek tek listeleniyor — ana firmada / bayide toplamak için tıkla"
                }
                aria-pressed={groupByMainFirm}
                className={`flex items-center justify-center px-2.5 sm:px-3 rounded-lg sm:rounded-xl shadow-sm border transition-colors bg-gradient-to-br ${
                  groupByMainFirm
                    ? "from-violet-50 to-purple-50 border-violet-200 hover:from-violet-100 hover:to-purple-100"
                    : "from-blue-50 to-indigo-50 border-blue-100 hover:from-blue-100 hover:to-indigo-100"
                }`}
              >
                <span className="flex flex-col items-center gap-0.5">
                  {groupByMainFirm ? (
                    <FaCity className="w-5 h-5 sm:w-6 sm:h-6 text-violet-600" />
                  ) : (
                    <FaBuilding className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                  )}
                  {/* Hangi görünümde olduğunu ikonun altında yazar —
                      iki kelime ALT ALTA (dar ikon çipine sığsın diye) */}
                  <span
                    className={`flex flex-col items-center text-[9px] font-SemiBold leading-tight ${
                      groupByMainFirm ? "text-violet-600" : "text-blue-600"
                    }`}
                  >
                    <span>{groupByMainFirm ? "Ana" : "Genel"}</span>
                    <span>Firmalar</span>
                  </span>
                </span>
              </button>
            )}
            <div className="min-w-0 flex flex-col justify-center">
              <h3 className="text-base sm:text-xl font-bold text-gray-900 whitespace-nowrap">
                {isDevices ? "Cihaz Tüketimleri" : "Firma Tüketimleri"}
              </h3>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                {PERIOD_LABELS[period] || period}
              </p>
            </div>
          </div>

          {/* Ürün filtre butonları — kayan pill; ikon + toplam + önceki döneme
              göre yüzde + ortalama. Yükleme sırasında skeleton ve tıklanamaz. */}
          <div className="relative grid grid-cols-4 gap-1 bg-gray-100 rounded-xl p-1 border border-gray-200 flex-1 sm:ml-auto sm:max-w-[460px]">
            {isFetching ? (
              PRODUCTS.map((product) => (
                <span
                  key={product.name}
                  className="h-[52px] sm:h-[58px] bg-gray-200 rounded-lg animate-pulse"
                />
              ))
            ) : (
              <>
                <span
                  className="absolute top-1 bottom-1 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 shadow-md transition-all duration-300 ease-in-out"
                  style={{
                    width: "calc((100% - 0.5rem) / 4)",
                    left: `calc(0.25rem + ${activeIndex} * ((100% - 0.5rem) / 4))`,
                  }}
                />
                {PRODUCTS.map((product) => {
                  const isActive = product.name === activeProduct;
                  const item = consumptionByProduct[product.name];
                  const total = item?.consument || 0;
                  const groupCount = isDevices ? item?.deviceCount : item?.firmCount;
                  const average =
                    groupCount > 0 ? parseFloat((total / groupCount).toFixed(1)) : 0;
                  const changePercent = performance?.[product.name]?.changePercent;
                  return (
                    <button
                      key={product.name}
                      type="button"
                      onClick={() => setActiveProduct(product.name)}
                      className={`relative z-10 flex flex-col items-center justify-center py-1 rounded-lg transition-colors duration-300 min-w-0 ${
                        isActive ? "text-white" : "text-gray-600 hover:text-gray-800"
                      }`}
                    >
                      <span className="flex items-center gap-1 min-w-0 max-w-full px-1">
                        <img
                          src={product.icon}
                          alt=""
                          className="w-3 h-3 sm:w-3.5 sm:h-3.5 object-contain shrink-0"
                          draggable={false}
                        />
                        <span className="text-[10px] sm:text-[11px] font-semibold truncate">
                          {product.label}
                        </span>
                      </span>
                      <span className="text-xs sm:text-sm font-extrabold tabular-nums leading-tight">
                        <NumberFlow
                          value={total}
                          format={NUMBER_FORMAT}
                          transformTiming={NUMBER_TIMING}
                          spinTiming={NUMBER_TIMING}
                        />
                      </span>
                      <span
                        className={`flex items-center gap-1 leading-none rounded px-1 py-0.5 ${
                          isActive ? "bg-white/90" : ""
                        }`}
                      >
                        <PerformanceBadge
                          changePercent={changePercent}
                          compact
                          className="text-[8px] sm:text-[9px]"
                        />
                        <span
                          className={`text-[8px] sm:text-[9px] font-semibold whitespace-nowrap ${
                            isActive ? "text-gray-600" : "text-gray-500"
                          }`}
                        >
                          Ort {average.toLocaleString("tr-TR")} Adt
                        </span>
                      </span>
                    </button>
                  );
                })}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Liste — sayfa başına 5 satır; min yükseklik sabit, satırlar kesilmeden
          sığmazsa kutu uzar (iki box grid'de eşit boyda kalır) */}
      <div className="relative flex-1 min-h-[350px] p-2.5 sm:p-3 bg-gray-50/60 flex flex-col">
        {isFetching ? (
          <div className="relative flex-1">
            <MascotLoadingOverlay
              message={isDevices ? "Cihaz tüketimleri yükleniyor..." : "Firma tüketimleri yükleniyor..."}
            />
            <div className="space-y-2 opacity-40">
              {Array.from({ length: PAGE_SIZE }).map((_, i) => (
                <div key={i} className="flex items-center justify-between gap-3 rounded-xl bg-white border border-gray-200 shadow-sm px-3 py-2.5">
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <div className="w-6 h-6 rounded-md bg-gray-200 animate-pulse shrink-0" />
                    <div className="w-8 h-8 rounded-lg bg-gray-200 animate-pulse shrink-0" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3.5 w-32 bg-gray-200 rounded animate-pulse" />
                      <div className="h-3 w-24 bg-gray-100 rounded animate-pulse" />
                    </div>
                  </div>
                  <div className="h-5 w-12 bg-gray-200 rounded animate-pulse shrink-0" />
                </div>
              ))}
            </div>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center select-none">
            <img src={mascotNoData} alt="Veri yok" className="w-20 h-20 sm:w-28 sm:h-28 object-contain mb-2 drop-shadow-lg" draggable={false} />
            <p className="text-sm font-bold text-gray-600">Bu dönemde tüketim bulunamadı.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {pagedRows.map((row, idx) => {
              // Satırdan detaya gidiş — id payload'da varsa satır link olur
              // (eski cache'lenmiş payload'larda id olmayabilir → düz satır).
              const targetId = isDevices ? row.deviceId : row.firmId;
              const rowInner = (
                <>
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="flex items-center justify-center w-6 h-6 rounded-md bg-gray-100 text-gray-500 text-xs font-bold shrink-0">
                      {(safePage - 1) * PAGE_SIZE + idx + 1}
                    </span>
                    {/* Satır ikonu: ana firmayla ilişkili satırlar MOR.
                        - Genel görünümde: üst firması olan (şube) firmalar
                        - Ana firma görünümünde: altına şube toplanmış satırlar
                        Diğerleri eski mavi/indigo kalır. */}
                    {(() => {
                      const group = groupByMainFirm ? null : groupOf(row);
                      const isMainFirmRelated =
                        !isDevices && (groupByMainFirm ? !!row.hasChildren : !!group);
                      // Bayi satırı (ya da genel görünümde bayiye bağlı satır)
                      // mağaza ikonuyla ayrışır (#205).
                      const isDealer =
                        !isDevices &&
                        (row.firmType === "dealer" || (!groupByMainFirm && group?.type === "dealer"));
                      const RowIcon = isDealer ? FaStore : isMainFirmRelated ? FaCity : Icon;
                      return (
                        <span
                          // Firma tipi renkleri tek kaynaktan (#215): bayi mavi, müşteri
                          // (ana firma dahil — ikon şekli ayırır) teal
                          className={`flex items-center justify-center w-8 h-8 rounded-lg shrink-0 border transition-colors ${
                            isDevices
                              ? "bg-indigo-50 text-indigo-600 border-transparent group-hover:bg-indigo-100 group-hover:border-indigo-300"
                              : isDealer
                                ? `${FIRM_TYPE_STYLES.dealer.iconBox} ${FIRM_TYPE_STYLES.dealer.iconBoxHover}`
                                : `${FIRM_TYPE_STYLES.customer.iconBox} ${FIRM_TYPE_STYLES.customer.iconBoxHover}`
                          }`}
                          title={
                            isMainFirmRelated
                              ? groupByMainFirm
                                ? `${isDealer ? "Bayi" : "Ana firma"} — ${row.firmCount} firmanın tüketimi burada toplandı`
                                : `${group?.type === "dealer" ? "Bayiye" : "Ana firmaya"} bağlı: ${group?.name}`
                              : isDealer
                                ? "Bayi"
                                : undefined
                          }
                        >
                          <RowIcon className="w-3.5 h-3.5" />
                        </span>
                      );
                    })()}
                    <div className="min-w-0">
                      {isDevices ? (
                        <>
                          <p className="font-semibold text-sm text-gray-900 break-all transition-colors group-hover:text-indigo-700 group-hover:underline underline-offset-2 decoration-indigo-400">
                            {row.serialNo}
                          </p>
                          <p className="text-xs text-gray-500 truncate flex items-center gap-1">
                            <FaBuilding className="w-3 h-3 shrink-0" /> {row.firmName}
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="font-semibold text-sm text-gray-900 break-words transition-colors group-hover:text-blue-700 group-hover:underline underline-offset-2 decoration-blue-400">
                            {row.firmName}
                            {row.bayserNo && <span className="ml-1 text-gray-400 font-normal group-hover:text-blue-400">#{row.bayserNo}</span>}
                            {/* Bayi etiketi — ikona ek olarak adın yanında (#191) */}
                            {row.firmType === "dealer" && (
                              <span className={`ml-1.5 inline-flex items-center align-middle px-1.5 py-px rounded border text-[10px] font-SemiBold no-underline ${FIRM_TYPE_STYLES.dealer.badge}`}>
                                Bayi
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <FaMicrochip className="w-3 h-3 shrink-0" /> {row.deviceCount ?? 0} cihaz
                            {/* Ana firma satırında kaç firmanın toplandığı —
                                tek şube olsa da yazar (mor ikon ayrımıyla
                                tutarlı olsun diye firmCount>1 koşulu kalktı) */}
                            {groupByMainFirm && row.hasChildren && (
                              <span
                                className={`inline-flex items-center gap-1 ${
                                  row.firmType === "dealer" ? FIRM_TYPE_STYLES.dealer.text : FIRM_TYPE_STYLES.customer.text
                                }`}
                              >
                                {row.firmType === "dealer" ? (
                                  <FaStore className="w-3 h-3 shrink-0" />
                                ) : (
                                  <FaCity className="w-3 h-3 shrink-0" />
                                )}
                                {row.firmCount} firma
                              </span>
                            )}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                    {/* Ana firma rozeti — firma bir şubeyse üst firmasını
                        yüzde istatistiğinin SOLUNDA gösterir (kullanıcı
                        isteği 2026-09-01). Cihaz kırılımında anlamı yok. */}
                    {(() => {
                      // Üst firma rozeti — genel görünümde satırın toplanacağı
                      // üst firma (bayi ise mağaza ikonlu). Cihaz kırılımında yok.
                      const group = isDevices || groupByMainFirm ? null : groupOf(row);
                      if (!group?.name) return null;
                      const dealer = group.type === "dealer";
                      const label = `${group.name}${group.bayserNo ? ` #${group.bayserNo}` : ""}`;
                      return (
                        <span
                          className={`hidden sm:inline-flex items-center gap-1 max-w-[140px] px-2 py-0.5 rounded-md border text-[10px] font-SemiBold whitespace-nowrap overflow-hidden ${
                            dealer ? FIRM_TYPE_STYLES.dealer.badge : FIRM_TYPE_STYLES.customer.badge
                          }`}
                          title={`${dealer ? "Bayi" : "Ana firma"}: ${label}`}
                        >
                          {dealer ? (
                            <FaStore className="w-2.5 h-2.5 shrink-0" />
                          ) : (
                            <FaBuilding className="w-2.5 h-2.5 shrink-0" />
                          )}
                          <span className="truncate">{label}</span>
                        </span>
                      );
                    })()}
                    <PerformanceBadge
                      changePercent={row.changePercent}
                      compact
                      className="text-[10px] sm:text-xs"
                    />
                    <span className={`text-base sm:text-lg font-extrabold ${isDevices ? "text-indigo-600" : "text-blue-600"}`}>
                      {Number(row.consumption ?? 0).toLocaleString("tr-TR")}
                    </span>
                  </div>
                </>
              );
              const rowClass =
                "flex items-center justify-between gap-3 rounded-xl bg-white border border-gray-200 shadow-sm px-3 py-2.5 hover:shadow-md transition-shadow";
              const rowKey = `${isDevices ? row.serialNo : row.firmName}-${idx}`;
              return targetId ? (
                <AppLink
                  key={rowKey}
                  to={`/Anasayfa/${isDevices ? "Cihazlar" : "Firma"}/${targetId}`}
                  state={{ fromPage: "Anasayfa/Dashboard" }}
                  className={`group ${rowClass}`}
                  title={isDevices ? "Cihaza git" : "Firmaya git"}
                >
                  {rowInner}
                </AppLink>
              ) : (
                <div key={rowKey} className={rowClass}>
                  {rowInner}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Sayfalama — drawer'larla aynı desen */}
      <div className="shrink-0 px-3 sm:px-4 py-2 sm:py-2.5 border-t border-gray-100 bg-white flex items-center justify-between">
        <p className="text-xs text-gray-400">
          {rows.length > 0
            ? `${(safePage - 1) * PAGE_SIZE + 1}-${Math.min(safePage * PAGE_SIZE, rows.length)} / ${rows.length}`
            : "0 / 0"}
        </p>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            disabled={safePage <= 1 || isFetching}
            onClick={() => setPage(safePage - 1)}
            className="px-2.5 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-40 transition-colors"
          >
            Önceki
          </button>
          <span className="px-2 py-1 text-xs font-medium text-gray-500 bg-gray-50 rounded-md">
            {safePage}/{totalPages}
          </span>
          <button
            type="button"
            disabled={safePage >= totalPages || rows.length === 0 || isFetching}
            onClick={() => setPage(safePage + 1)}
            className="px-2.5 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-40 transition-colors"
          >
            Sonraki
          </button>
        </div>
      </div>
    </div>
  );
}

export default React.memo(ConsumptionBreakdownCard);
